// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { ComboboxItem, SelectProps } from '@mantine/core';
import { Select } from '@mantine/core';
import type { Practitioner, Reference } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useMemo } from 'react';
import type { AssignableProviderOption } from '../../hooks/useAssignableProviders';
import { useAssignableProviders } from '../../hooks/useAssignableProviders';

export interface ProviderSelectProps
  extends Omit<SelectProps, 'data' | 'value' | 'defaultValue' | 'onChange' | 'searchable'> {
  /** Currently assigned provider, e.g. `Task.owner`. */
  value: Reference | undefined;
  onChange: (value: Reference<Practitioner> | undefined) => void;
}

/**
 * Picks an individual clinic provider as a task owner. Only physicians, NPs,
 * and care coordinators are listed; a current value outside that list (legacy
 * owner, or the signed-in user) is still shown so it is not silently dropped.
 */
export function ProviderSelect({ value, onChange, placeholder, ...selectProps }: ProviderSelectProps): JSX.Element {
  const { providers, loading, error } = useAssignableProviders();

  const data = useMemo((): ComboboxItem[] => {
    const options: ComboboxItem[] = providers.map((provider: AssignableProviderOption) => ({
      value: provider.value,
      label: provider.label,
    }));
    const current = value?.reference;
    if (current && !options.some((option: ComboboxItem) => option.value === current)) {
      options.unshift({ value: current, label: value.display ?? 'Current assignee' });
    }
    return options;
  }, [providers, value]);

  const handleChange = (next: string | null): void => {
    if (!next) {
      onChange(undefined);
      return;
    }
    const provider = providers.find((option: AssignableProviderOption) => option.value === next);
    onChange({ reference: next, display: provider?.label ?? value?.display });
  };

  return (
    <Select
      {...selectProps}
      data={data}
      value={value?.reference ?? null}
      onChange={handleChange}
      placeholder={loading ? 'Loading providers…' : (placeholder ?? 'Select provider')}
      searchable
      nothingFoundMessage="No matching providers"
      error={selectProps.error ?? (error ? 'Could not load providers' : undefined)}
    />
  );
}
